// AsientosListXL - Sprint 44 Fase 3 Vista 8.
// Lista asientos con filtros (estado, modulo, q debounced) + pager + msg.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import useAsientosXL from '../hooks/useAsientosXL';
import XLTable from '../../../components/xl/XLTable';
import XLInput from '../../../components/xl/XLInput';
import XLSelect from '../../../components/xl/XLSelect';
import XLBadge from '../../../components/xl/XLBadge';
import {
  fmt, ESTADO_VARIANT, ESTADO_LABEL, ESTADO_OPTIONS, MODULO_OPTIONS,
} from '../utils';

const CSS_ID = 'xl-asientos-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__sub { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid var(--xl-color-border);
  background: transparent; color: var(--xl-color-text-secondary); cursor: pointer;
}
.xl-btn:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse); border-color: transparent }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }

.xl-filters {
  display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 12px;
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
}
@media (max-width: 720px) { .xl-filters { grid-template-columns: 1fr } }
.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px;
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger); border: 1px solid rgba(220,38,38,0.28) }
.xl-pager { display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 4px; margin-top: 12px; color: var(--xl-color-text-secondary); font-size: 13px }
.xl-pager__group { display: flex; gap: 8px; align-items: center }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
`;

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

export default function AsientosListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const navigate = useNavigate();
  const ax = useAsientosXL();
  const [searchLocal, setSearchLocal] = useDebouncedSetter(ax.setQ, 300);

  const columns = useMemo(() => ([
    { key: 'numero', label: 'Numero',
      render: (a) => <span className="xl-mono">{a.numero || '(borrador)'}</span> },
    { key: 'fecha', label: 'Fecha', render: (a) => a.fecha || '\u2014' },
    { key: 'descripcion', label: 'Descripcion',
      render: (a) => a.descripcion || '\u2014' },
    { key: 'modulo_origen', label: 'Modulo',
      render: (a) => a.modulo_origen || '\u2014' },
    { key: 'total_debito', label: 'Debito', align: 'right',
      render: (a) => <span className="xl-mono">{fmt(a.total_debito)}</span> },
    { key: 'total_credito', label: 'Credito', align: 'right',
      render: (a) => <span className="xl-mono">{fmt(a.total_credito)}</span> },
    { key: 'estado', label: 'Estado',
      render: (a) => (
        <XLBadge variant={ESTADO_VARIANT[a.estado] || 'neutral'} dot>
          {ESTADO_LABEL[a.estado] || a.estado}
        </XLBadge>
      ) },
  ]), []);

  const { page, totalPages, total, canPrev, canNext, loading } = ax;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Asientos contables</h2>
          <p className="xl-page__sub">Registro contable de la empresa.</p>
        </div>
        <button type="button" className="xl-btn xl-btn--primary"
          onClick={() => navigate('/contabilidad/asientos/nuevo')}>
          + Nuevo asiento
        </button>
      </header>

      <section className="xl-filters" aria-label="Filtros de asientos">
        <XLInput type="search" label="Buscar"
          placeholder="Numero, descripcion o referencia..."
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)} />
        <XLSelect label="Estado" value={ax.estado}
          onChange={(e) => ax.setEstado(e.target.value)} options={ESTADO_OPTIONS} />
        <XLSelect label="Modulo" value={ax.modulo}
          onChange={(e) => ax.setModulo(e.target.value)} options={MODULO_OPTIONS} />
      </section>

      {ax.err && <div className="xl-banner" role="alert">{ax.err}</div>}

      <XLTable
        ariaLabel="Lista de asientos"
        columns={columns}
        data={ax.items}
        loading={loading}
        onRowClick={(a) => navigate('/contabilidad/asientos/' + a.id)}
        rowKey="id"
        emptyMessage="Sin asientos para estos filtros." />

      <div className="xl-pager">
        <span>{total
          ? ('Pagina ' + page + ' de ' + totalPages + ' \u00b7 ' + total + ' resultados')
          : 'Sin resultados'}</span>
        <span className="xl-pager__group">
          <button type="button" className="xl-btn"
            disabled={!canPrev || loading}
            onClick={() => ax.setPage(page - 1)}>Anterior</button>
          <span className="xl-mono">{page} / {totalPages}</span>
          <button type="button" className="xl-btn"
            disabled={!canNext || loading}
            onClick={() => ax.setPage(page + 1)}>Siguiente</button>
        </span>
      </div>
    </div>
  );
}
